import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Avatar from "@/components/base/Avatar";
import Toast from "@/components/feature/Toast";
import EmptyState from "@/components/feature/EmptyState";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useSeo } from "@/hooks/useSeo";
import { useToast } from "@/hooks/useToast";
import { videos } from "@/mocks/videos";
import { getCategoryName, formatViews } from "@/utils/format";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const [tab, setTab] = useState<"uploads" | "favorites">("uploads");
  const [favorites, setFavorites] = useLocalStorage<string[]>("gg_favorites", []);
  const [likes] = useLocalStorage<string[]>("gg_likes", []);

  useSeo({
    title: "个人中心 - 呱呱视频社区",
    description: "查看你在呱呱视频社区的个人资料、已上传视频与收藏列表，管理你的社区互动记录。",
    keywords: "个人中心,呱呱视频社区,我的收藏,我的上传",
  });

  const uploads = useMemo(() => videos.filter((v) => v.authorType === "user").slice(0, 4), []);
  const favoriteVideos = useMemo(
    () => favorites.map((id) => videos.find((v) => v.id === id)).filter(Boolean),
    [favorites],
  );

  const removeFavorite = (id: string) => {
    setFavorites(favorites.filter((x) => x !== id));
    showToast("已取消收藏", "info");
  };

  const logout = () => {
    showToast("已退出登录（演示）", "info");
    window.setTimeout(() => navigate("/"), 600);
  };

  const stats = [
    { value: uploads.length, label: "上传视频" },
    { value: favorites.length, label: "我的收藏" },
    { value: likes.length, label: "点赞过" },
  ];

  const list = tab === "uploads" ? uploads : favoriteVideos;

  return (
    <div className="max-w-[1080px] mx-auto px-4 md:px-6 py-8">
      <div className="p-5 md:p-7 rounded-lg bg-background-100 border border-background-200">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <Avatar name="呱" size="lg" tone="primary" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading font-extrabold text-xl text-foreground-950">呱呱小达人</h1>
              <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs">普通用户</span>
            </div>
            <p className="mt-1 text-sm text-foreground-600">
              热爱记录生活，也喜欢发现别人镜头里的美好。欢迎来我的主页逛逛～
            </p>
            <div className="mt-4 flex items-center gap-6">
              {stats.map((s) => (
                <div key={s.label}>
                  <p className="font-heading font-extrabold text-lg text-primary-600">{s.value}</p>
                  <p className="text-xs text-foreground-500">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full border border-background-300 text-sm text-foreground-700 hover:border-accent-400 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap self-start"
          >
            <i className="ri-logout-box-r-line" aria-hidden="true"></i>
            退出登录
          </button>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-1 p-1 rounded-full bg-background-100 w-fit">
        {([
          { key: "uploads", label: "我的上传", icon: "ri-video-line" },
          { key: "favorites", label: "我的收藏", icon: "ri-bookmark-line" },
        ] as const).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-1.5 h-9 px-5 rounded-full text-sm cursor-pointer transition-colors whitespace-nowrap ${
              tab === t.key ? "bg-primary-500 text-background-50 font-semibold" : "text-foreground-600 hover:text-primary-600"
            }`}
          >
            <i className={t.icon} aria-hidden="true"></i>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {list.length > 0 ? (
          <ul className="space-y-3">
            {list.map((v) =>
              v ? (
                <li
                  key={v.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-lg bg-background-50 border border-background-200"
                >
                  <Link to={`/video/${v.id}`} className="relative w-full sm:w-40 aspect-video rounded overflow-hidden bg-background-200 shrink-0 group">
                    <img
                      src={v.cover}
                      alt={v.title}
                      title={`${v.title} - 呱呱视频社区`}
                      className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform"
                    />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/video/${v.id}`} className="font-medium text-foreground-950 hover:text-primary-600 transition-colors line-clamp-2">
                      {v.title}
                    </Link>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-price-tag-3-line" aria-hidden="true"></i>
                        {getCategoryName(v.category)}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line" aria-hidden="true"></i>
                        {v.publishDate}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-play-circle-line" aria-hidden="true"></i>
                        {formatViews(v.views)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:flex-col sm:items-end">
                    <Link
                      to={`/video/${v.id}`}
                      className="inline-flex items-center gap-1 h-9 px-4 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-play-fill" aria-hidden="true"></i>
                      观看
                    </Link>
                    {tab === "favorites" && (
                      <button
                        type="button"
                        onClick={() => removeFavorite(v.id)}
                        className="inline-flex items-center gap-1 h-9 px-4 rounded-full border border-background-300 text-xs text-foreground-600 hover:border-accent-400 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-bookmark-fill" aria-hidden="true"></i>
                        取消收藏
                      </button>
                    )}
                  </div>
                </li>
              ) : null,
            )}
          </ul>
        ) : (
          <EmptyState
            title={tab === "uploads" ? "还没有上传视频" : "收藏夹还是空的"}
            description={
              tab === "uploads"
                ? "上传你的第一个作品，让更多人看到你的分享。"
                : "在视频详情页点击收藏，就能在这里快速找到喜欢的内容。"
            }
            actionLabel={tab === "uploads" ? "去上传视频" : "去逛逛"}
            onAction={() => navigate(tab === "uploads" ? "/upload" : "/")}
          />
        )}
      </div>

      <Toast toast={toast} onClose={hideToast} />
    </div>
  );
}